import { ClassType } from '@frontend/common';
import { StatusBadge } from '@frontend/components/common';
import { DeceasedState, DeceasedStateName } from '@frontend/deceased/types';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

/** Aligns with `DeceasedState.getType` (DARK → bg-foreground-default). */
export const DECEASED_STATUS_COLOR_MAP = new Map<string[], ClassType>([
    [[DeceasedStateName.ANY], ClassType.DISABLED],
    [[DeceasedStateName.REGISTERED], ClassType.INFO],
    [[DeceasedStateName.WAITING_FOR_CREMATION], ClassType.DARK],
    [[DeceasedStateName.IN_CREMATION], ClassType.WARNING],
    [[DeceasedStateName.CREMATED, DeceasedStateName.RELEASED], ClassType.SUCCESS],
    [[DeceasedStateName.CANCELLED], ClassType.DANGER]
]);

const DECEASED_STATUS_MESSAGE_IDS: Record<DeceasedStateName, { id: string; defaultMessage: string; description: string }> = {
    [DeceasedStateName.ANY]: { id: 'DeceasedState.any', defaultMessage: 'Any state', description: 'The displayed value for the DeceasedState any state' },
    [DeceasedStateName.REGISTERED]: {
        id: 'DeceasedState.registered',
        defaultMessage: 'Registered',
        description: 'The displayed value for the DeceasedState registered'
    },
    [DeceasedStateName.WAITING_FOR_CREMATION]: {
        id: 'DeceasedState.waiting_for_cremation',
        defaultMessage: 'Waiting for cremation',
        description: 'The displayed value for the DeceasedState waiting for cremation'
    },
    [DeceasedStateName.IN_CREMATION]: {
        id: 'DeceasedState.in_cremation',
        defaultMessage: 'In cremation',
        description: 'The displayed value for the DeceasedState in cremation'
    },
    [DeceasedStateName.CREMATED]: {
        id: 'DeceasedState.cremated',
        defaultMessage: 'Cremated',
        description: 'The displayed value for the DeceasedState cremated'
    },
    [DeceasedStateName.RELEASED]: {
        id: 'DeceasedState.released',
        defaultMessage: 'Released',
        description: 'The displayed value for the DeceasedState released'
    },
    [DeceasedStateName.CANCELLED]: {
        id: 'DeceasedState.cancelled',
        defaultMessage: 'Cancelled',
        description: 'The displayed value for the DeceasedState cancelled'
    }
};

export function useDeceasedStatusDisplayMap(): Map<string, string> {
    const intl = useIntl();
    return useMemo(
        () =>
            new Map<string, string>(
                (Object.keys(DECEASED_STATUS_MESSAGE_IDS) as DeceasedStateName[]).map((status) => [
                    status,
                    intl.formatMessage(DECEASED_STATUS_MESSAGE_IDS[status])
                ])
            ),
        [intl]
    );
}

export interface DeceasedStatusBadgeProps {
    status: DeceasedStateName | string;
}

export const DeceasedStatusBadge = ({ status }: DeceasedStatusBadgeProps) => {
    const displayMap = useDeceasedStatusDisplayMap();
    const displayMapWithFallback = useMemo(() => {
        if (displayMap.has(status)) return displayMap;
        return new Map(displayMap).set(status, DeceasedState.format(status));
    }, [displayMap, status]);

    return (
        <StatusBadge
            value={status}
            colorMap={DECEASED_STATUS_COLOR_MAP}
            displayMap={displayMapWithFallback}
        />
    );
};
